/*
 * Copyright (c) 2024 Ask Sage, Inc. All rights reserved.
 * This software is the confidential and proprietary information of Ask Sage, Inc.
 * You shall not disclose such confidential information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Ask Sage, Inc.
 * THIS SOFTWARE IS PROVIDED "AS IS" AND ANY EXPRESSED OR IMPLIED WARRANTIES,
 * INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND
 * FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL ASK SAGE, INC.
 * OR ITS CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL,
 * EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT
 * OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS
 * INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT,
 * STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY
 * OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 * You may not use this software except in compliance with the License.
 * You may not modify, reverse engineer, or redistribute this software.
 */
import React, { useState, useEffect } from 'react';
import { isAdminLoggedIn, getUser } from '../AuthService.js'
import { FiMenu, FiTrash2, FiFile, FiLink, FiPlus, FiFileText } from 'react-icons/fi';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, useTheme, Drawer, IconButton, Stack, Checkbox } from '@mui/material';
import { IoClose } from 'react-icons/io5';
import { MarkdownRenderer } from '../Chat/MarkdownRenderer';

const SideMenu = ({ 
    workbook,
    sources,
    setSideBarOpen, 
    sideBarOpen,
    hideSideBar, 
    deleteSource,
    setHideSideBar,
    setTriggerFilePopup,
    selectedSources,
    setSelectedSources
  }) => {
  const [modalIsOpen, setIsOpen] = useState(false);
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);
  const [messageKey, setMessageKey] = useState(null);
  const [selectedSource, setSelectedSource] = useState(null);
  const theme = useTheme();
  const [isMobile, setIsMobile] = useState(window.innerWidth < theme.breakpoints.values.md);

  useEffect(() => {
    if (sources !== null) {
      const initialSelectedSources = sources.reduce((acc, source) => {
        acc[source.id] = source;
        return acc;
      }, {});
      setSelectedSources(initialSelectedSources);
    }
  }, [sources]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < theme.breakpoints.values.md);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [theme.breakpoints.values.md]);

  function showFilePopup() {
    setTriggerFilePopup(1);
  }

  async function confirmDelete(e) {
    e.stopPropagation();

    await deleteSource(messageKey);

    setMessageKey(null);
    setDeleteModalIsOpen(false);
  }

  function handleSourceClick(source) {
    setSelectedSource(source);
    setIsOpen(true);
  }

  function handleDeleteClick(e, sourceId) {
    e.stopPropagation();
    setMessageKey(sourceId);
    setDeleteModalIsOpen(true);
  }

  function handleCheckboxChange(e, source) {
    setSelectedSources(prevSelectedSources => {
      const newSelectedSources = { ...prevSelectedSources };
      if (e.target.checked) {
        newSelectedSources[source.id] = source;
      } else {
        delete newSelectedSources[source.id];
      }
      return newSelectedSources;
    });
  }

  return (
    <>
      {
        isMobile && (
          <Drawer 
            anchor='left' 
            open={sideBarOpen} 
            onClose={()=>{setSideBarOpen(false)}}
            sx={{
              '& .MuiPaper-root': {
                backgroundColor: theme.palette.appBackground,
                backgroundImage: 'none',
                boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.2)',
              }
            }}
          >
            <Box padding={1} sx={{maxHeight: '100dvh', width: '300px'}}>
              <Stack direction='row' justifyContent='space-between' alignItems='center'>
                <IconButton onClick={()=>{setSideBarOpen(false)}} >
                  <FiMenu />
                </IconButton>
                <IconButton onClick={()=>{
                  showFilePopup();
                  setSideBarOpen(false);
                }}>
                  <FiPlus />
                </IconButton>
              </Stack>
              <div className="workbook-side-menu-list-button" style={{ height: 'calc(100dvh - 54px)', overflowY: 'scroll'}}>
                {sources !== null && sources.map((source, index) => (
                  <Source
                    workbook={workbook} 
                    key={index} 
                    source={source} 
                    deleteClick={(e) => handleDeleteClick(e, source.id)} 
                    buttonClick={() => handleSourceClick(source)} 
                    isChecked={!!selectedSources[source.id]} 
                    onCheckboxChange={handleCheckboxChange}
                  />
                ))}
              </div>
            </Box>
          </Drawer>
      )}
      {!isMobile && (
        <Box className="workbook-side-menu"  width={{width: hideSideBar ? '50px' : '260px'}}>
          <Stack 
            direction={ hideSideBar ? 'column' : 'row'} 
            justifyContent='space-between' 
            alignItems={hideSideBar ? 'start' : 'center'}
          >
            <IconButton size='small' onClick={()=>{ setHideSideBar(!hideSideBar) }} >
              <FiMenu />
            </IconButton>
            {hideSideBar ? 
              <IconButton size='small' onClick={()=>{ showFilePopup() }} >
                <FiPlus />
              </IconButton> 
              :
              <Button startIcon={<FiPlus />} variant='outlined' size={'small'} onClick={showFilePopup} color={'buttonColor'}>
                Sources
              </Button>
            } 
          </Stack>
          { !hideSideBar && <div className="workbook-side-menu-list-button">
            {sources !== null && sources.map((source, index) => (
              <Source 
                workbook={workbook}
                key={index} 
                source={source} 
                deleteClick={(e) => handleDeleteClick(e, source.id)} 
                buttonClick={() => handleSourceClick(source)} 
                isChecked={!!selectedSources[source.id]} 
                onCheckboxChange={handleCheckboxChange}
              />
            ))}
          </div>}
        </Box>
      )}
      <Dialog
        open={modalIsOpen}
        onClose={()=>{setIsOpen(false)}}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="lg"
        fullWidth
        className="workbook-side-menu-dialog"
      >
        <DialogTitle id="alert-dialog-title">
        <Box display={'flex'} justifyContent={'space-between'}>
          Source Details
          <IconButton onClick={()=>{setIsOpen(false)}} className="workspace-icon-button">
            <IoClose style={{color: 'black'}} />
          </IconButton>
        </Box>          
        </DialogTitle>
        <DialogContent>
          {selectedSource && (
            <>
              <p><strong>Identifier:</strong> {selectedSource.identifier}</p>
              <p><strong>Type:</strong> {selectedSource.source_type}</p>
              <div style={{ maxHeight: '450px', overflowY: 'auto', marginBottom: '20px', backgroundColor: '#121212', borderRadius: '5px', color: 'white' }}>
                <div className="markdownrenderer-div" style={{ padding: '10px' }}>
                  <p style={{fontSize: '0.9em'}}><strong>Summary:</strong></p>
                  <MarkdownRenderer style={{fontSize: '0.9em'}} content={selectedSource.summary} user='bot' />
                </div>
              </div>

              <div style={{ maxHeight: '250px', overflowY: 'scroll' }}>
                <p style={{margin: '10px', fontSize: '0.9em'}}><strong>Full Text:</strong> {selectedSource.full_text}</p>
              </div>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>{setIsOpen(false)}}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={deleteModalIsOpen}
        onClose={()=>{setDeleteModalIsOpen(false)}}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className="workbook-side-menu-dialog"
      >
        <DialogTitle id="alert-dialog-title">Delete Source</DialogTitle>
        <DialogContent>
          <p>Are you sure you want to delete this source?</p>
        </DialogContent>
        <DialogActions>
          <Button color={'error'} variant='contained' onClick={confirmDelete}>Delete</Button>
          <Button onClick={()=>{setDeleteModalIsOpen(false)}}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

const Source = ({ workbook, source, buttonClick, deleteClick, isChecked, onCheckboxChange }) => {
  const theme = useTheme();
  const getSourceIcon = (type) => {
    switch(type) {
      case 'filename':
        return <FiFile />;
      case 'url':
        return <FiLink />;
      case 'plain_text':
        return <FiFileText />;
      default:
        return null;
    }
  }

  const hasDeleteAccess = () => {
    if(workbook === null || workbook === undefined) {
      return false;
    }
    var cur_user = getUser()
    if(cur_user === null || cur_user === undefined) {
      return false;
    }
    if(workbook.user_id === cur_user.id) {
      return true;
    }
    if(isAdminLoggedIn() === true) {
      return true;
    }
    return false;
  };

  return (
    <Box 
      className={"workbook-side-menu-source"} 
      data-key={source.id} 
      onClick={buttonClick} 
      sx={{
        backgroundColor: theme.palette.card.backgroundColor,
        '&:hover': {
          backgroundColor: theme.palette.card.hover.backgroundColor,
        }
      }}
    >
      <Checkbox 
        checked={isChecked} 
        onChange={(e) => onCheckboxChange(e, source)} 
        onClick={(e) => e.stopPropagation()} 
      />
      <div className="workbook-side-menu-source-icon" data-key={source.id}>{getSourceIcon(source.source_type)}</div>
      <div className="workbook-side-menu-source-name" data-key={source.id}>{source.identifier}</div>
      <div className='workbook-side-menu-button-source-group'>        
        {hasDeleteAccess() &&
        <IconButton onClick={deleteClick}><FiTrash2 size={18} /></IconButton> }
      </div>
    </Box>
  );
}
export default SideMenu;